import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify";

/** Contact interface */
interface Contact {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

/** Payload for creating a contact */
interface CreateContactPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/** Generic error parser */
const parseErrorResponse = async (
  response: Response
): Promise<{ message: string }> => {
  try {
    const data = await response.json();
    return { message: data.message || "An error occurred" };
  } catch {
    return { message: response.statusText || "An error occurred" };
  }
};

export const contactService = {
  /**
   * Creates a new contact.
   * Endpoint: POST /contacts
   */
  createContact: async (data: CreateContactPayload): Promise<Contact> => {
    const url = `${awsConfig.apiEndpoint}/contacts`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to create contact");
      }

      const contact = await response.json();
      return contact;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves all contacts (requires token).
   * Endpoint: GET /contacts
   */
  getContacts: async (token: string): Promise<Contact[]> => {
    const url = `${awsConfig.apiEndpoint}/contacts`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: 'cors',
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch contacts");
      }

      const data = await response.json();
      if (!data.success || !Array.isArray(data.contacts)) {
        throw new Error("Invalid response format from server");
      }

      return data.contacts;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves a single contact by ID (requires token).
   * Endpoint: GET /contacts/{id}
   */
  getContactById: async (token: string, contactId: string): Promise<Contact> => {
    const url = `${awsConfig.apiEndpoint}/contacts/${contactId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: 'cors',
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch contact");
      }

      const data = await response.json();
      if (!data.success || !data.contact) {
        throw new Error("Invalid response format from server");
      }

      return data.contact;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Updates a contact (requires token).
   * Endpoint: PUT /contacts/{id}
   */
  updateContact: async (
    token: string,
    contactId: string,
    data: Partial<Contact>
  ): Promise<Contact> => {
    const url = `${awsConfig.apiEndpoint}/contacts/${contactId}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to update contact");
      }
      return response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Deletes a contact (requires token).
   * Endpoint: DELETE /contacts/{id}
   */
  deleteContact: async (
    token: string,
    contactId: string
  ): Promise<{ success: boolean }> => {
    const url = `${awsConfig.apiEndpoint}/contacts/${contactId}`;
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to delete contact");
      }
      return response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },
};
